import React, { useEffect, useState } from "react";
import useNotification from "../../../hooks/components/useNotification";
import { QUERY_CHECK_DEPLOYMENT_STATUS } from "../../../../apollo/module-operations/deployment";
import { Icon } from "@/components";
import { StoreStatus, storeStatusIcons } from "@/ui";
import { useStoreContext } from "context";
import { useLazyQuery } from "@apollo/react-hooks";
import { useUser } from "@/hooks";

import storeStatusStyles from "../../../../ui/components/StoreStatus/StoreStatus.module.scss";

const DeploymentStatus = () => {
    const initialState = {
        statusName: "READY",
        lastUpdate: "",
        icon: "status-success",
    };
    const { store } = useStoreContext();
    const { accountId, storeId } = useUser();
    const notify = useNotification();

    const [buildingStatus, setBuildingStatus] = useState(false);
    const [purgeLoading, setPurgeLoading] = useState(false);
    const [status, setStatus] = useState(initialState);
    const statusErrors = ["ERROR"];
    const statusBuilding = ["BUILDING", "INITIALIZING", "QUEUED"];

    const [checkDeploymentStatus, { loading: loadingStatus, refetch }] =
        useLazyQuery(QUERY_CHECK_DEPLOYMENT_STATUS, {
            skip: !accountId || !storeId,
            notifyOnNetworkStatusChange: true,
            variables: {
                account_id: accountId,
                store_id: storeId,
            },
            onCompleted: resp => {
                const data = resp?.checkDeploymentStatus;
                if (statusBuilding.includes(data?.status)) {
                    monitorDeployment(true);
                    return;
                }

                if (data?.status !== status?.statusName) {
                    getFormatStatus(data);
                }
            },
            onError: resp => {
                setStatus({
                    statusName: "ERROR",
                    lastUpdate: "",
                    icon: getStatusIcon("ERROR"),
                });
                notify(resp?.message, "error");
            },
        });

    const activeLoading = buildingStatus || loadingStatus || purgeLoading;

    const handleRunDeployment = () => {
        fetch(store?.provider_deployment_url)
            .then(response => {
                if (!response.ok) {
                    notify(
                        "An error occurs while running the deployment, please try again later",
                        "error",
                    );
                }
                return response.json();
            })
            .catch(error => {
                notify(error.message, "error");
            });
    };

    const handlePurgeCache = async () => {
        const url = `${store.domain}/api/revalidate`;
        if (store.domain) {
            await fetch(url, {
                method: "POST",
            })
                .then(response => {
                    if (!response.ok) {
                        notify(
                            "An error occurs while running the deployment, please try again later",
                            "error",
                        );
                        return;
                    }
                    setPurgeLoading(false);
                    notify("The cache was updated successfully", "success");
                    return response.json();
                })
                .catch(error => {
                    setPurgeLoading(false);
                    notify(error.message, "error");
                });
        } else {
            setPurgeLoading(false);
            notify("Please configure the store domain", "warning");
        }
    };

    const monitorDeployment = async deployment => {
        try {
            setBuildingStatus(true);
            setStatus({
                statusName: "BUILDING",
                lastUpdate: "",
                icon: "status-building",
            });
            if (!deployment) {
                handleRunDeployment();
            }
            const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

            let checkStatus = "BUILDING";
            while (checkStatus === "BUILDING") {
                await delay(90000);

                const check = await refetch();

                if (check) {
                    const value = check?.data?.checkDeploymentStatus?.status;
                    if (statusBuilding.includes(value)) {
                        checkStatus = "BUILDING";
                    } else {
                        checkStatus =
                            check?.data?.checkDeploymentStatus?.status;
                    }
                }
            }

            setBuildingStatus(false);
            if (checkStatus !== "ERROR" && checkStatus !== "CANCELED") {
                notify(
                    `Deployment completed with status: ${checkStatus}`,
                    "success",
                );
                handlePurgeCache();
                return;
            }
            notify(`Failed deployment`, "error");
        } catch (error) {
            console.error("Error en el proceso de deployment:", error);
        }
    };

    const getStatusIcon = statusKey => {
        if (statusKey === "READY") {
            return "status-success";
        }

        if (statusErrors.includes(statusKey)) {
            return "status-error";
        }

        return "status-building";
    };

    const getStatusName = () => {
        if (status.statusName === "READY") {
            return "Online";
        }

        if (status.statusName === "ERROR") {
            return "Error";
        }

        return "Building";
    };

    const getFormatStatus = data => {
        if (!data?.status) {
            setStatus(initialState);
            return;
        }

        if (data.status === "READY" || data.status === "CANCELED") {
            setStatus({
                statusName: "READY",
                icon: "status-success",
                time: "",
            });
            return;
        }

        if (statusErrors.includes(data.status)) {
            setStatus({
                statusName: "ERROR",
                icon: "status-error",
                time: "",
            });
            return;
        }

        setStatus({
            statusName: "BUILDING",
            icon: "status-building",
            time: "",
        });
        return;
    };

    useEffect(() => {
        checkDeploymentStatus();
    }, [store]);

    const tone =
        status.statusName === "ERROR"
            ? "danger"
            : status.statusName === "BUILDING"
            ? "warning"
            : "default";

    const showSpinner = loadingStatus || buildingStatus || purgeLoading;

    const label =
        (loadingStatus || purgeLoading) && !buildingStatus
            ? "Loading"
            : getStatusName();

    const triggerIcon = showSpinner ? (
        <span className={storeStatusStyles.iconWell} data-loading="">
            <span aria-hidden className={storeStatusStyles.statusSpinner} />
        </span>
    ) : (
        <span className={storeStatusStyles.iconWell} data-tone={tone}>
            <Icon icon={status.icon || "status-error"} />
        </span>
    );

    const actions = [
        {
            id: "publish",
            label: "Publish",
            variant: "primary",
            icon: storeStatusIcons.publish,
            disabled: !store?.provider_deployment_url || activeLoading,
            onSelect: () => monitorDeployment(false),
        },
        {
            id: "purge",
            label: "Purge Cache",
            variant: "secondary",
            icon: storeStatusIcons.purge,
            disabled: activeLoading,
            onSelect: () => {
                setPurgeLoading(true);
                handlePurgeCache();
            },
        },
        {
            id: "check",
            label: "Check Status",
            variant: "secondary",
            icon: storeStatusIcons.refresh,
            disabled: activeLoading,
            onSelect: () => refetch(),
        },
    ];

    return (
        <StoreStatus
            actions={actions}
            aria-label={`Store deployment status, ${label}`}
            icon={triggerIcon}
            label={label}
            loading={showSpinner}
            metaLabel="Status store"
            tone={tone}
        />
    );
};

export default DeploymentStatus;
